import { useMemo } from 'react';
import {
  useGetBoardWorkflowTasksQuery,
  useGetTaskStatusesQuery,
  useGetTimelineQuery,
} from 'app/services/projectBoardApi';

/**
 * Project Status chip content (shared /static/mvc/view/project.status.js →
 * project.status.tmpl), inventory §3.4 / parity 37–38.
 *
 * Text = `Project Status : {task} : {status}` where {task} is the name of the
 * HIGHEST task_id whose timeline status_id > 1 (and which exists in the CMS
 * task catalog); default `{tasks_arranged[1], 'not started'}`. Renders only
 * after tasks + statuses are fetched (the Backbone view waits on both).
 *
 * The hidden #checkbox_status_changed is rendered for DOM parity; the Workflow
 * popup's post-Done `trigger('change')` refresh is wired in the workflow
 * sub-phase via `Timeline` tag invalidation (this chip re-renders from the
 * same RTK cache — replaces the Backbone re-fetch-on-every-render).
 *
 * [NEG] honored: the `listenTo(self.project, …)` no-op (self.project
 * undefined) is not reproduced.
 */
const ProjectStatusChip = ({ projectId }: { projectId: number }) => {
  const { data: tasks } = useGetBoardWorkflowTasksQuery();
  const { data: statuses } = useGetTaskStatusesQuery();
  const { data: timeline } = useGetTimelineQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });

  const statusBar = useMemo(() => {
    if (!tasks || !statuses) return null;
    const tasksById: Record<number, string> = {};
    tasks.forEach((t) => {
      tasksById[t.id] = t.name;
    });
    const statusesById: Record<number, string> = {};
    statuses.forEach((s) => {
      statusesById[s.id] = s.name;
    });

    let minTaskId = 0;
    let maxTask = { task: tasksById[1] ?? '', status: 'not started' };
    (timeline ?? []).forEach((row) => {
      const statusId = row.status_id ?? 0;
      if (statusId > 1 && row.task_id > minTaskId && tasksById[row.task_id]) {
        minTaskId = row.task_id;
        maxTask = {
          task: tasksById[row.task_id],
          status: statusesById[statusId] ?? '',
        };
      }
    });
    return maxTask;
  }, [tasks, statuses, timeline]);

  if (!statusBar) return null;

  return (
    <>
      <span className="status">
        Project Status : {statusBar.task} : {statusBar.status}
      </span>
      <input type="checkbox" id="checkbox_status_changed" className="hidden" defaultChecked={false} />
    </>
  );
};

export default ProjectStatusChip;
